/**
 * NLPTranscriptDialog Component
 * 
 * Dialog for managing NLP transcription tasks for video posts including:
 * - Display of last NLP session status
 * - Button to start new NLP transcription task
 * - Update button to check task status (disabled for now)
 * - Real-time status display with timestamps
 * 
 * Used in: VideoPostTranscriptManager component
 */
import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Alert,
    Chip,
    Paper,
    CircularProgress,
    Grid
} from '@mui/material';
import { 
    Psychology, 
    PlayArrow, 
    Refresh, 
    CheckCircle, 
    Error as ErrorIcon,
    Pending as PendingIcon,
    Warning as WarningIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import PropTypes from 'prop-types';
import { userRequest } from '../../requestMethods';

const NLPTranscriptDialog = ({ open, onClose, videoPost, onStatusUpdate }) => {
    const [loading, setLoading] = useState(false);
    const [deletingTranscripts, setDeletingTranscripts] = useState(false);
    const [alert, setAlert] = useState({ show: false, message: '', severity: 'info' });
    
    // Check if video post has existing transcripts
    const hasExistingTranscripts = videoPost?.transcripts && videoPost.transcripts.length > 0;

    const showAlert = (message, severity = 'info') => {
        setAlert({ show: true, message, severity });
        setTimeout(() => setAlert({ show: false, message: '', severity: 'info' }), 6000);
    };

    const handleDeleteAllTranscripts = async () => {
        if (!videoPost?._id) {
            showAlert('No video post selected', 'error');
            return;
        }

        const confirmed = window.confirm(
            `⚠️ WARNING: This will permanently delete all ${videoPost.transcripts.length} existing transcripts and their translations. This action cannot be undone.\n\nAre you sure you want to continue?`
        );

        if (!confirmed) {
            return;
        }

        setDeletingTranscripts(true);
        try {
            // Delete all transcripts for this video post
            const response = await userRequest.delete(`/contentVideoPost/${videoPost._id}/transcripts/all`);
            
            const deletedCount = response.data.deletedCount || 0;
            showAlert(`Successfully deleted all ${deletedCount} transcripts. You can now start the NLP task.`, 'success');
            
            // Call status update to refresh the video post data
            if (onStatusUpdate) {
                onStatusUpdate({
                    transcripts: [],
                    shouldRefresh: true // Signal parent to refresh data
                });
            }
            
        } catch (error) {
            console.error('Error deleting transcripts:', error);
            const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message;
            showAlert(`Error deleting transcripts: ${errorMessage}`, 'error');
        } finally {
            setDeletingTranscripts(false);
        }
    };

    const handleStartNLPTask = async () => {
        if (!videoPost?._id) {
            showAlert('No video post selected', 'error');
            return;
        }

        // Check if there are existing transcripts
        if (hasExistingTranscripts) {
            showAlert('Please delete existing transcripts first before starting NLP task', 'warning');
            return;
        }

        setLoading(true);
        try {
            const response = await userRequest.post('/nlp/transcribe-video-post', {
                videoPostId: videoPost._id
            });

            if (response.data.success) {
                showAlert(response.data.message, 'success');
                
                // Call the status update callback if provided
                if (onStatusUpdate) {
                    const description = `NLP transcription task started at ${new Date().toISOString()}${response.data.document_id ? `. Document ID: ${response.data.document_id}` : ''}`;
                    onStatusUpdate({
                        nlpTranscriptStatus: true,
                        nlpDescription: description
                    });
                }
            } else {
                showAlert(response.data.message || 'Failed to start NLP task', 'error');
            }
        } catch (error) {
            console.error('Error starting NLP task:', error);
            const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message;
            showAlert(`Error starting NLP task: ${errorMessage}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateStatus = () => {
        // For now, this is disabled as per requirements
        showAlert('Status update feature coming soon', 'info');
    };

    const getStatusChip = () => {
        if (!videoPost) return null;

        if (videoPost.nlpTranscriptStatus) {
            return (
                <Chip
                    icon={<CheckCircle />}
                    label="NLP Task Active"
                    color="primary"
                    variant="outlined"
                />
            );
        } else {
            return (
                <Chip
                    icon={<PendingIcon />}
                    label="No Active Task"
                    color="default"
                    variant="outlined"
                />
            );
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'N/A';
        
        try {
            const date = new Date(timestamp);
            return date.toLocaleString();
        } catch (error) {
            return timestamp;
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box display="flex" alignItems="center" gap={1}>
                    <Psychology color="primary" />
                    <Typography variant="h6">
                        NLP Transcription Manager
                    </Typography>
                </Box>
            </DialogTitle>
            
            <DialogContent>
                {alert.show && (
                    <Alert severity={alert.severity} sx={{ mb: 3 }}>
                        {alert.message}
                    </Alert>
                )}

                {/* Video Post Info */}
                <Paper sx={{ p: 2, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Video Post: {videoPost?.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        ID: {videoPost?._id}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Language: {videoPost?.language?.name || 'Unknown'}
                    </Typography>
                </Paper>

                {/* NLP Status Section */}
                <Paper sx={{ p: 2, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        NLP Session Status
                    </Typography>
                    
                    <Grid container spacing={2} alignItems="center">
                        <Grid item>
                            {getStatusChip()}
                        </Grid>
                        <Grid item xs>
                            <Typography variant="body2" color="text.secondary">
                                Last updated: {formatTimestamp(videoPost?.updatedAt)}
                            </Typography>
                        </Grid>
                    </Grid>

                    {videoPost?.nlpDescription && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Description:
                            </Typography>
                            <Typography variant="body2" sx={{ 
                                p: 1, 
                                bgcolor: 'grey.50', 
                                borderRadius: 1,
                                fontFamily: 'monospace',
                                fontSize: '0.875rem'
                            }}>
                                {videoPost.nlpDescription}
                            </Typography>
                        </Box>
                    )}
                </Paper>

                {/* Transcript Warning Section */}
                {hasExistingTranscripts && (
                    <Alert severity="warning" sx={{ mb: 2 }} icon={<WarningIcon />}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Warning:</strong> This video post has {videoPost.transcripts.length} existing transcript{videoPost.transcripts.length !== 1 ? 's' : ''}.
                        </Typography>
                        <Typography variant="body2">
                            NLP transcription will replace all existing transcripts. You must delete them first before proceeding.
                        </Typography>
                    </Alert>
                )}

                {/* Actions Section */}
                <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        Actions
                    </Typography>
                    
                    <Box display="flex" gap={2} flexDirection="column">
                        {hasExistingTranscripts ? (
                            <Button
                                variant="contained"
                                color="error"
                                startIcon={deletingTranscripts ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
                                onClick={handleDeleteAllTranscripts}
                                disabled={deletingTranscripts}
                                fullWidth
                                sx={{ py: 1.5 }}
                            >
                                {deletingTranscripts ? 'Deleting Transcripts...' : `Delete All ${videoPost.transcripts.length} Transcripts`}
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                startIcon={loading ? <CircularProgress size={20} /> : <PlayArrow />}
                                onClick={handleStartNLPTask}
                                disabled={loading}
                                fullWidth
                                sx={{ py: 1.5 }}
                            >
                                {loading ? 'Starting NLP Task...' : 'Start NLP Task'}
                            </Button>
                        )}
                        
                        <Button
                            variant="outlined"
                            startIcon={<Refresh />}
                            onClick={handleUpdateStatus}
                            disabled={true} // Disabled as per requirements
                            fullWidth
                            sx={{ py: 1.5 }}
                        >
                            Update Status (Coming Soon)
                        </Button>
                    </Box>
                </Paper>

                {/* Information Box */}
                <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                        The NLP transcription process will analyze your video post and generate new transcript data. 
                        {hasExistingTranscripts 
                            ? ' Since you have existing transcripts, you must delete them first as NLP will replace all transcript data.'
                            : ' This process runs on our NLP server and may take some time to complete.'
                        }
                    </Typography>
                </Alert>
            </DialogContent>
            
            <DialogActions>
                <Button onClick={onClose}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

NLPTranscriptDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    videoPost: PropTypes.object,
    onStatusUpdate: PropTypes.func
};

export default NLPTranscriptDialog; 